import {
  Drama,
  Hand,
  MonitorPlay,
  Music,
  PartyPopper,
  Trophy,
  type LucideIcon,
} from 'lucide-react';
import type { ResponsiveValue } from '@chakra-ui/react';

/**
 * Floating pills shown around the phone mockup in the About hero (TZ-384), matching the
 * Figma "Hero" design: حفلات موسيقية، رياضة، مهرجانات، تجربة سلسة، ورشات، مسرح. Most pills are
 * soft pastel surfaces with a saturated icon/text; مهرجانات is the one solid-dark accent pill.
 * Colors are literal design values (decorative to this hero only — not theme tokens).
 */
export interface CategoryPill {
  id: string;
  /** react-intl id; copy lives in src/translations/ar.json. */
  labelId: string;
  defaultLabel: string;
  Icon: LucideIcon;
  surface: string;
  iconColor: string;
  textColor: string;
  /** Static tilt (degrees) applied to the pill, per the design. */
  rotate: number;
}

export const CATEGORY_PILLS: CategoryPill[] = [
  {
    id: 'music',
    labelId: 'about.hero.category.music',
    defaultLabel: 'Music concerts',
    Icon: Music,
    surface: '#EADDF7', // soft purple
    iconColor: '#6533AC',
    textColor: '#1B1917',
    rotate: 7,
  },
  {
    id: 'sports',
    labelId: 'about.hero.category.sports',
    defaultLabel: 'Sports',
    Icon: Trophy,
    surface: '#D8F3DF', // soft mint green
    iconColor: '#22C55E',
    textColor: '#1B1917',
    rotate: -5,
  },
  {
    id: 'festivals',
    labelId: 'about.hero.category.festivals',
    defaultLabel: 'Festivals',
    Icon: PartyPopper,
    surface: '#1B1917', // solid dark accent pill
    iconColor: '#FF5C4F',
    textColor: '#FFFFFF',
    rotate: -6,
  },
  {
    id: 'smoothExperience',
    labelId: 'about.hero.category.smoothExperience',
    defaultLabel: 'Smooth experience',
    Icon: Hand,
    surface: '#FBD5CF', // soft coral
    iconColor: '#FF5C4F',
    textColor: '#1B1917',
    rotate: -8,
  },
  {
    id: 'workshops',
    labelId: 'about.hero.category.workshops',
    defaultLabel: 'Workshops',
    Icon: MonitorPlay,
    surface: '#DCE7FB', // soft blue
    iconColor: '#3B82F6',
    textColor: '#1B1917',
    rotate: 5,
  },
  {
    id: 'theater',
    labelId: 'about.hero.category.theater',
    defaultLabel: 'Theater',
    Icon: Drama,
    surface: '#FBEFBF', // soft amber
    iconColor: '#F59E0B',
    textColor: '#1B1917',
    rotate: 6,
  },
];

/**
 * Per-pill placement, index-aligned to {@link CATEGORY_PILLS}: three pills float down the
 * left of the phone, three down the right. On `lg+` they sit well outside the phone edges
 * ("around the mockup"); on smaller screens they hug the phone edges ("on the mockup").
 * Physical left/right is intentional — placement is decorative, so it stays mirrored
 * relative to the centered phone regardless of RTL.
 */
type PillPosition = {
  top?: ResponsiveValue<string>;
  bottom?: ResponsiveValue<string>;
  left?: ResponsiveValue<string>;
  right?: ResponsiveValue<string>;
};

export const PILL_POSITIONS: PillPosition[] = [
  // music — left, upper
  { top: { base: '2%', lg: '12%' }, left: { base: '-6px', lg: '-178px' } },
  // sports — left, middle
  { top: { base: '40%', lg: '46%' }, left: { base: '-10px', lg: '-200px' } },
  // festivals — left, lower
  { bottom: { base: '12%', lg: '8%' }, left: { base: '-4px', lg: '-150px' } },
  // smoothExperience — right, upper
  { top: { base: '2%', lg: '6%' }, right: { base: '-6px', lg: '-168px' } },
  // workshops — right, middle
  { top: { base: '42%', lg: '50%' }, right: { base: '-8px', lg: '-188px' } },
  // theater — right, lower
  { bottom: { base: '8%', lg: '4%' }, right: { base: '-4px', lg: '-150px' } },
];
